import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowDownIcon, ArrowUpIcon } from 'lucide-react';
import { builders } from '../../data/builders';
import { cca2Of, countryNames } from '../../data/geography';
import { CountryEcoRow, ecosystemPivot } from '../../utils/ecosystem';
import { SourceRef } from '../SourceRef';
import { AvailabilityMatrix } from './AvailabilityMatrix';
type Key = 'name' | 'models' | 'openWeight' | 'builders' | 'platforms' | 'compute';
const COLS: Array<{
  key: Key;
  label: string;
}> = [{
  key: 'name',
  label: 'Country'
}, {
  key: 'models',
  label: 'Models released'
}, {
  key: 'openWeight',
  label: 'Open-weight'
}, {
  key: 'builders',
  label: 'Builders'
}, {
  key: 'platforms',
  label: 'Model platforms'
}, {
  key: 'compute',
  label: 'Compute providers'
}];
function nameMap(): Record<string, {
  name: string;
  cca2: string | null;
}> {
  const out: Record<string, {
    name: string;
    cca2: string | null;
  }> = {};
  builders.forEach(b => b.cca3 && (out[b.cca3] = {
    name: b.country,
    cca2: b.cca2?.toLowerCase() ?? null
  }));
  Object.entries(countryNames).forEach(([k, v]) => out[k] = {
    name: v,
    cca2: cca2Of[k] ?? out[k]?.cca2 ?? null
  });
  return out;
}
export function CountryViewTab() {
  const rows = useMemo(() => ecosystemPivot(nameMap()), []);
  const [key, setKey] = useState<Key>('builders');
  const [dir, setDir] = useState<'asc' | 'desc'>('desc');
  const sorted = useMemo(() => {
    const s = [...rows].sort((a: CountryEcoRow, b: CountryEcoRow) => key === 'name' ? a.name.localeCompare(b.name) : a[key] - b[key]);
    return dir === 'asc' ? s : s.reverse();
  }, [rows, key, dir]);
  const sortBy = (k: Key) => {
    if (k === key) setDir(d => d === 'asc' ? 'desc' : 'asc');else {
      setKey(k);
      setDir(k === 'name' ? 'asc' : 'desc');
    }
  };
  return <>
      <section aria-labelledby="pivot-title" data-id="element-747" data-mp="ych88hy50ytm">
        <h2 id="pivot-title" className="font-serif text-2xl text-ink md:text-3xl" data-id="element-748" data-mp="u9u8luv869lv">
          The stack, by country
          <SourceRef id="censusCurated" data-id="element-749" data-mp="2tf43tstidf26" />
        </h2>
        <p className="mt-2 max-w-2xl text-[14px] text-muted" data-id="element-750" data-mp="2ycniuuy15vv6">Each country’s share of the AI ecosystem, counted from our builders, models and platforms datasets by headquarters. Counts cover only what we track.</p>
        <div className="mt-5 overflow-x-auto rounded-lg border border-line" data-id="element-751" data-mp="38bmjbq8n6l73">
          <table className="w-full min-w-[760px] border-collapse text-[13.5px]" data-id="element-752" data-mp="3ccokicm4d7bp">
            <thead className="bg-surface" data-id="element-753" data-mp="3caluvtovpg7i">
              <tr data-id="element-754" data-mp="3haztkl4lbdsv">
                {COLS.map(c => <th key={c.key} scope="col" aria-sort={key === c.key ? dir === 'asc' ? 'ascending' : 'descending' : 'none'} className={`whitespace-nowrap border-b border-line px-3 py-2.5 font-normal ${c.key === 'name' ? 'text-left' : 'text-right'}`} data-id="element-755" data-mp="3h9osit2j4k5g">
                    <button type="button" onClick={() => sortBy(c.key)} className={`inline-flex items-center gap-1 text-[12px] ${key === c.key ? 'text-ink' : 'text-muted hover:text-ink'}`} data-id="element-756" data-mp="3w1p6brxp90yu">
                      {c.label}
                      {key === c.key ? dir === 'asc' ? <ArrowUpIcon className="h-3 w-3" data-id="element-757" data-mp="1kdbykggd48cj" /> : <ArrowDownIcon className="h-3 w-3" data-id="element-758" data-mp="1kc99me2lofz9" /> : <span className="w-3" data-id="element-759" data-mp="1kfezhvj1zlw1" />}
                    </button>
                  </th>)}
              </tr>
            </thead>
            <tbody className="font-mono tabular" data-id="element-760" data-mp="1afxwjlv6rm3r">
              {sorted.map(r => <tr key={r.cca3} className="border-b border-line/60 last:border-b-0 hover:bg-surface" data-id="element-761" data-mp="283eoor0pv48i">
                  <td className="px-3 py-2 font-sans" data-id="element-762" data-mp="233a8z37mudjc">
                    <Link to={`/countries/${r.cca3}`} className="inline-flex items-center gap-2 text-ink hover:text-machine" data-id="element-763" data-mp="235wnxnwurvbl">
                      {r.cca2 && <img src={`https://flagcdn.com/${r.cca2}.svg`} alt="" loading="lazy" className="h-3.5 w-5 rounded-[2px] object-cover ring-1 ring-line" data-id="element-764" data-mp="1teikj51s6bd6" />}
                      {r.name}
                    </Link>
                  </td>
                  {(['models', 'openWeight', 'builders', 'platforms', 'compute'] as const).map(k => <td key={k} className={`px-3 py-2 text-right ${r[k] === 0 ? 'text-faint' : 'text-ink'}`} data-id="element-765" data-mp="26jd2tqaf9six">
                      {r[k]}
                    </td>)}
                </tr>)}
            </tbody>
          </table>
        </div>
      </section>
      <AvailabilityMatrix data-id="element-766" data-mp="21mcybjt80xtx" />
    </>;
}